import React, { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { useTheme } from "../contexts/ThemeContext";
import { useToast } from "./ToastNotifications";
import {
  Palette, Copy, Download, Eye, Shuffle, Info as InfoIcon,
  Wand2, Image as ImageIcon, Bookmark, PanelRight, Monitor
} from "lucide-react";

// Components
import ActivityBar from "./ActivityBar";
import CommandPalette from "./CommandPalette";
import ColorBlindnessBanner from "./ColorBlindnessBanner";
import ColorPaletteGenerator from "./ColorPaletteGenerator";
import PaletteTabs from "./PaletteTabs";
import PaletteBuilder from "./PaletteBuilder";
import ImageExtractor from "./ImageExtractor";
import SavedPalettes from "./SavedPalettes";
import Toolbar from "./Toolbar";
import Visualizer from "./Visualizer/Visualizer";
import InfoModal from "./InfoModal";
import FaviconUpdater from "./FaviconUpdater";
import Footer from "./Footer";
import OnboardingOverlay from './OnboardingOverlay';
import BackToTop from './BackToTop';
import LoadingScreen from './LoadingScreen';
import SEO from './SEO';
import { generateRandomColor, generatePalettes, generateShades } from "../lib/colorUtils";

const ColorApp = () => {
  const { isDark, setIsDark } = useTheme();
  const { addToast } = useToast();

  // Activity Bar State
  const [activeSection, setActiveSection] = useState('info');
  const [hasNewVisualizer, setHasNewVisualizer] = useState(() => {
    const saved = localStorage.getItem('hasNewVisualizer');
    return saved === 'true';
  });
  const [hasNewSavedPalette, setHasNewSavedPalette] = useState(() => {
    const saved = localStorage.getItem('hasNewSavedPalette');
    return saved === 'true';
  });

  // Command Palette State
  const [isCommandPaletteOpen, setIsCommandPaletteOpen] = useState(false);

  // Onboarding State
  const [isOnboardingActive, setIsOnboardingActive] = useState(false);

  // Loading State
  const [isLoading, setIsLoading] = useState(false); // Disabled loading screen to fix endless loading issue

  // Mobile Detection State
  const [isMobile, setIsMobile] = useState(false);
  const [showMobileBanner, setShowMobileBanner] = useState(true);

  // Color State
  const [baseColor, setBaseColor] = useState(() => generateRandomColor());
  const [tempColor, setTempColor] = useState(() => generateRandomColor());

  // UI State
  const [visibleFields, setVisibleFields] = useState({
    colorName: true,
    hex: false,
    rgb: false,
    hsl: false,
    contrastRatio: false,
  });

  const [colorBlindnessType, setColorBlindnessType] = useState("normal");
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);

  // History State
  const [colorHistory, setColorHistory] = useState(() => {
    const savedHistory = localStorage.getItem("colorHistory");
    return savedHistory ? JSON.parse(savedHistory) : [];
  });

  // Palette State
  const [palettes, setPalettes] = useState({});
  const [shades, setShades] = useState([]);

  // Saved Palettes State (persisted)
  const [savedPalettes, setSavedPalettes] = useState(() => {
    const saved = localStorage.getItem('savedPalettes');
    return saved ? JSON.parse(saved) : [];
  });

  // Image Extractor State (persists across section switches)
  const [extractedPalette, setExtractedPalette] = useState([]);

  // Palette Builder State (persists across section switches)
  const [builderPalette, setBuilderPalette] = useState([]);

  // Custom Palette State (for toolbar custom palette - from context menu)
  const [customPalette, setCustomPalette] = useState(() => {
    const saved = localStorage.getItem('customPalette');
    return saved ? JSON.parse(saved) : [];
  });

  // Visualizer State
  const [visualizerColors, setVisualizerColors] = useState([
    { id: "color-1", value: "#4287f5" },
    { id: "color-2", value: "#FF5733" },
    { id: "color-3", value: "#33FF57" },
    { id: "color-4", value: "#3357FF" },
    { id: "color-5", value: "#FF33F1" },
  ]);

  const [showVisualizerAlert, setShowVisualizerAlert] = useState(false);

  // Effects
  useEffect(() => {
    const initialColor = generateRandomColor();
    setBaseColor(initialColor);
    setTempColor(initialColor);
  }, []);

  // Hide loading screen after progress bar fills
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 3000); // Show loader for 3 seconds (2.8s progress bar + 0.2s buffer)

    return () => clearTimeout(timer);
  }, []);

  // Mobile Detection - Detects actual mobile/tablet devices
  useEffect(() => {
    const checkMobile = () => {
      // Check user agent for mobile devices
      const userAgent = navigator.userAgent || navigator.vendor || window.opera;
      const isMobileDevice = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent.toLowerCase());

      // Also check screen width as fallback
      const isSmallScreen = window.innerWidth < 768;

      setIsMobile(isMobileDevice || isSmallScreen);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    localStorage.setItem("colorHistory", JSON.stringify(colorHistory));
  }, [colorHistory]);

  useEffect(() => {
    localStorage.setItem("savedPalettes", JSON.stringify(savedPalettes));
  }, [savedPalettes]);

  useEffect(() => {
    localStorage.setItem("customPalette", JSON.stringify(customPalette));
  }, [customPalette]);

  // Persist badge states
  useEffect(() => {
    localStorage.setItem('hasNewVisualizer', hasNewVisualizer.toString());
  }, [hasNewVisualizer]);

  useEffect(() => {
    localStorage.setItem('hasNewSavedPalette', hasNewSavedPalette.toString());
  }, [hasNewSavedPalette]);

  // Command Palette - Keyboard Shortcut (Cmd+K / Ctrl+K)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsCommandPaletteOpen(prev => !prev);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Clear visualizer badge when user opens it
  useEffect(() => {
    if (activeSection === 'visualizer') {
      setHasNewVisualizer(false);
    }
  }, [activeSection]);

  // Clear saved palettes badge when user opens it
  useEffect(() => {
    if (activeSection === 'saved') {
      setHasNewSavedPalette(false);
    }
  }, [activeSection]);

  // Generate palettes whenever tempColor changes (real-time updates while dragging)
  useEffect(() => {
    const newPalettes = generatePalettes(tempColor);
    const newShades = generateShades(tempColor);
    setPalettes(newPalettes);
    setShades(newShades);
  }, [tempColor]);

  // Handlers
  const resetColorBlindness = () => {
    setColorBlindnessType("normal");
  };

  const handleColorChange = (color) => {
    setTempColor(color);
  };

  const handleColorChangeComplete = (color) => {
    setBaseColor(color);
    setTempColor(color);
    updateColorHistory(color);
  };

  const updateColorHistory = (color) => {
    setColorHistory((prevHistory) => {
      if (prevHistory[0] !== color) {
        const newHistory = [
          color,
          ...prevHistory.filter((c) => c !== color),
        ];
        return newHistory;
      }
      return prevHistory;
    });
  };

  const clearColorHistory = () => {
    setColorHistory([]);
    localStorage.removeItem("colorHistory");
    addToast("Color history cleared", "success");
  };

  const onEyeDropperSelect = (color) => {
    handleColorChangeComplete(color);
  };

  const addColorToPalette = useCallback((newColor) => {
    setVisualizerColors((prevColors) => {
      const colorExists = prevColors.some((color) => color.value === newColor);

      if (colorExists) {
        return prevColors;
      } else {
        const newColorObj = { id: `color-${Date.now()}`, value: newColor };
        return [...prevColors, newColorObj];
      }
    });
  }, []);

  const addColorToCustomPalette = useCallback((newColor) => {
    setCustomPalette((prevPalette) => {
      const colorExists = prevPalette.includes(newColor);

      if (colorExists) {
        addToast('Color already in custom palette', 'info');
        return prevPalette;
      } else {
        addToast('Added to custom palette', 'success');
        return [...prevPalette, newColor];
      }
    });
  }, [addToast]);

  const setVisualizerColorsWithAlert = useCallback((colors) => {
    setVisualizerColors(colors);
    setShowVisualizerAlert(true); // Keep this for backward compatibility
    setHasNewVisualizer(true); // Set badge

    // Show toast with action button
    addToast(
      'Palette sent to Visualizer',
      'success',
      4000,
      {
        label: 'Open Visualizer →',
        onClick: () => {
          setActiveSection('visualizer');
          setHasNewVisualizer(false); // Clear badge when opening
        }
      }
    );
  }, [addToast]);

  const closeVisualizerAlert = () => {
    setShowVisualizerAlert(false);
    setActiveSection('visualizer');
  };

  const savePalette = useCallback((paletteName, colors) => {
    const newPalette = {
      id: Date.now(),
      name: paletteName,
      colors: colors,
      createdAt: new Date().toISOString()
    };

    setSavedPalettes(prev => [newPalette, ...prev]);

    // Show success notification
    addToast(`Saved "${paletteName}" with ${colors.length} colors`, 'success');

    // Set badge for saved palettes
    setHasNewSavedPalette(true);
  }, [addToast]);

  const handleSaveCustomPalette = useCallback(() => {
    if (!customPalette || customPalette.length === 0) {
      addToast('No colors in custom palette to save', 'error');
      return;
    }

    // Use default name but add a number if there are existing palettes with the same name
    const baseName = 'My Custom Palette';
    const existingNames = savedPalettes.map(p => p.name);
    let paletteName = baseName;
    let counter = 2;

    while (existingNames.includes(paletteName)) {
      paletteName = `${baseName} ${counter}`;
      counter++;
    }

    savePalette(paletteName, customPalette);
    // Clear the custom palette after saving
    setCustomPalette([]);
    localStorage.setItem('hasNewCustomPaletteColors', 'false');
  }, [customPalette, savedPalettes, savePalette, addToast]);

  const handleExportCustomPaletteToVisualizer = useCallback(() => {
    if (!customPalette || customPalette.length === 0) {
      addToast('No colors in custom palette to export', 'error');
      return;
    }

    const colorsForVisualizer = customPalette.map((color, index) => ({
      id: `custom-color-${Date.now()}-${index}`,
      value: color
    }));

    setVisualizerColorsWithAlert(colorsForVisualizer);
  }, [customPalette, setVisualizerColorsWithAlert, addToast]);

  const handleClearCustomPalette = useCallback(() => {
    setCustomPalette([]);
    localStorage.setItem('hasNewCustomPaletteColors', 'false');
    addToast('Custom palette cleared', 'success');
  }, [addToast]);

  // Command Palette Commands
  const commands = [
    {
      id: 'switch-info',
      label: 'Show Color Information',
      description: 'View color history and detailed information',
      icon: InfoIcon,
      action: () => setActiveSection('info'),
      keywords: ['color', 'info', 'history', 'details']
    },
    {
      id: 'switch-palettes',
      label: 'Show Generated Palettes',
      description: 'View harmony-based color palettes',
      icon: Palette,
      action: () => setActiveSection('palettes'),
      keywords: ['palette', 'harmony', 'colors']
    },
    {
      id: 'switch-builder',
      label: 'Open Palette Builder',
      description: 'Build custom color palettes',
      icon: Wand2,
      action: () => setActiveSection('builder'),
      keywords: ['build', 'custom', 'create']
    },
    {
      id: 'switch-extractor',
      label: 'Open Image Extractor',
      description: 'Extract colors from images',
      icon: ImageIcon,
      action: () => setActiveSection('extractor'),
      keywords: ['image', 'extract', 'photo', 'upload']
    },
    {
      id: 'switch-saved',
      label: 'Open Saved Palettes',
      description: 'Save and manage your color palettes',
      icon: Bookmark,
      action: () => setActiveSection('saved'),
      keywords: ['saved', 'palettes', 'manage', 'bookmark', 'favorites']
    },
    {
      id: 'switch-visualizer',
      label: 'Open Visualizer',
      description: 'Preview colors in UI designs',
      icon: PanelRight,
      action: () => setActiveSection('visualizer'),
      keywords: ['visualizer', 'preview', 'design', 'ui']
    },
    {
      id: 'toggle-theme',
      label: isDark ? 'Switch to Light Mode' : 'Switch to Dark Mode',
      description: 'Toggle between light and dark themes',
      icon: Eye,
      action: () => setIsDark(!isDark),
      shortcut: 'T',
      keywords: ['theme', 'dark', 'light', 'mode']
    },
    {
      id: 'random-color',
      label: 'Generate Random Color',
      description: 'Generate a new random color',
      icon: Shuffle,
      action: () => {
        const newColor = generateRandomColor();
        handleColorChangeComplete(newColor);
      },
      shortcut: 'R',
      keywords: ['random', 'generate', 'new']
    },
    {
      id: 'copy-hex',
      label: 'Copy HEX Code',
      description: 'Copy the current color HEX code',
      icon: Copy,
      action: () => {
        navigator.clipboard.writeText(baseColor);
      },
      shortcut: 'C',
      keywords: ['copy', 'hex', 'code']
    }
  ];

  // Render content based on active section
  const renderActiveContent = () => {
    switch (activeSection) {
      case 'info':
        return (
          <ColorPaletteGenerator
            baseColor={baseColor}
            tempColor={tempColor}
            onColorChange={handleColorChange}
            onColorChangeComplete={handleColorChangeComplete}
            colorBlindnessType={colorBlindnessType}
            isDark={isDark}
            colorHistory={colorHistory}
            addColorToCustomPalette={addColorToCustomPalette}
            clearColorHistory={clearColorHistory}
          />
        );

      case 'palettes':
        return (
          <PaletteTabs
            palettes={palettes}
            shades={shades}
            selectedColor={tempColor}
            onColorClick={handleColorChangeComplete}
            visibleFields={visibleFields}
            colorBlindnessType={colorBlindnessType}
            isDark={isDark}
            addColorToPalette={addColorToPalette}
            setVisualizerColors={setVisualizerColorsWithAlert}
            savePalette={savePalette}
            addColorToCustomPalette={addColorToCustomPalette}
          />
        );

      case 'builder':
        return (
          <PaletteBuilder
            selectedColor={tempColor}
            onColorClick={handleColorChangeComplete}
            visibleFields={visibleFields}
            colorBlindnessType={colorBlindnessType}
            isDark={isDark}
            addColorToPalette={addColorToPalette}
            setVisualizerColors={setVisualizerColorsWithAlert}
            savePalette={savePalette}
            builderPalette={builderPalette}
            setBuilderPalette={setBuilderPalette}
            addColorToCustomPalette={addColorToCustomPalette}
          />
        );

      case 'extractor':
        return (
          <ImageExtractor
            selectedColor={tempColor}
            onColorClick={handleColorChangeComplete}
            visibleFields={visibleFields}
            colorBlindnessType={colorBlindnessType}
            isDark={isDark}
            addColorToPalette={addColorToPalette}
            setVisualizerColors={setVisualizerColorsWithAlert}
            savePalette={savePalette}
            extractedPalette={extractedPalette}
            setExtractedPalette={setExtractedPalette}
            addColorToCustomPalette={addColorToCustomPalette}
          />
        );

      case 'saved':
        return (
          <SavedPalettes
            selectedColor={tempColor}
            onColorClick={handleColorChangeComplete}
            visibleFields={visibleFields}
            colorBlindnessType={colorBlindnessType}
            isDark={isDark}
            addColorToPalette={addColorToPalette}
            setVisualizerColors={setVisualizerColorsWithAlert}
            savedPalettes={savedPalettes}
            setSavedPalettes={setSavedPalettes}
            addColorToCustomPalette={addColorToCustomPalette}
            customPalette={customPalette}
            setCustomPalette={setCustomPalette}
          />
        );

      case 'visualizer':
        return (
          <Visualizer
            isOpen={true}
            onClose={() => setActiveSection('info')}
            colors={visualizerColors}
            onColorsChange={setVisualizerColors}
            colorBlindnessType={colorBlindnessType}
            isDark={isDark}
            inline={true}
            savedPalettes={savedPalettes}
          />
        );

      default:
        return null;
    }
  };

  return (
    <>
      <SEO
        title="Komorebi - Professional Color Palette Generator & Design Tool"
        description="Free professional color palette generator with 14+ industry standards (RAL, Pantone, HKS, NCS, Munsell), WCAG accessibility checker, color blindness simulation, and instant exports. Create beautiful color harmonies with advanced color theory tools."
        canonical="https://ko-mo-re-bi.com/app"
        keywords="color palette generator app, color picker tool, RAL colors, Pantone matching, WCAG checker, color blindness simulator, color harmony generator, professional color tool, design system colors"
      />
      <FaviconUpdater color={tempColor} />

      {/* Skip to content link for screen readers */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-20 focus:z-[60] focus:px-4 focus:py-2 focus:bg-blue-600 focus:text-white focus:rounded-lg focus:shadow-lg"
      >
        Skip to main content
      </a>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        transition={{ duration: 0.4, ease: "easeInOut" }}
        className="min-h-screen bg-gray-100 dark:bg-gray-900 text-gray-900 dark:text-gray-100 transition-all duration-300 ease-in-out flex flex-col"
      >
        <ColorBlindnessBanner
          colorBlindnessType={colorBlindnessType}
          onReset={resetColorBlindness}
        />

        {/* Mobile Warning Banner */}
        {isMobile && showMobileBanner && (
          <motion.div
            role="alert"
            aria-live="polite"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className="relative z-40 mt-4 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 border-b border-amber-200 dark:border-amber-800"
          >
            <div className="max-w-7xl mx-auto px-4 py-3">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 flex-1">
                  <Monitor className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0" aria-hidden="true" />
                  <p className="text-sm text-amber-900 dark:text-amber-100">
                    <span className="font-semibold">Best experienced on desktop.</span>
                    <span className="hidden sm:inline"> Some features may be limited on mobile devices.</span>
                  </p>
                </div>
                <button
                  onClick={() => setShowMobileBanner(false)}
                  className="text-amber-600 dark:text-amber-400 hover:text-amber-800 dark:hover:text-amber-200 transition-colors px-2 py-1 text-sm font-medium"
                  aria-label="Dismiss mobile warning"
                >
                  Dismiss
                </button>
              </div>
            </div>
          </motion.div>
        )}

        {/* Main Layout: Activity Bar + Content */}
        <div className="flex-1 flex overflow-hidden">
          {/* Activity Bar (Fixed) */}
          <ActivityBar
            activeSection={activeSection}
            onSectionChange={setActiveSection}
            onStartOnboarding={() => setIsOnboardingActive(true)}
            hasNewVisualizer={hasNewVisualizer}
            hasNewSavedPalette={hasNewSavedPalette}
          />

          {/* Main Content Area with left padding for fixed activity bar */}
          <main
            id="main-content"
            role="main"
            aria-label={`${activeSection === 'info' ? 'Color Information' : activeSection === 'palettes' ? 'Generated Palettes' : activeSection === 'builder' ? 'Palette Builder' : activeSection === 'extractor' ? 'Image Extractor' : activeSection === 'saved' ? 'Saved Palettes' : activeSection === 'visualizer' ? 'Color Visualizer' : 'Main Content'}`}
            className="flex-1 overflow-y-auto bg-gray-100 dark:bg-gray-900 ml-16"
          >
            <div className="max-w-7xl mx-auto px-6 py-6 pb-24">
              {renderActiveContent()}
            </div>
          </main>
        </div>

        {/* Toolbar (Bottom Floating) */}
        <Toolbar
          isDark={isDark}
          setIsDark={setIsDark}
          visibleFields={visibleFields}
          setVisibleFields={setVisibleFields}
          colorBlindnessType={colorBlindnessType}
          setColorBlindnessType={setColorBlindnessType}
          baseColor={baseColor}
          tempColor={tempColor}
          onColorChange={handleColorChange}
          onColorChangeComplete={handleColorChangeComplete}
          onEyeDropperSelect={onEyeDropperSelect}
          visualizerColors={visualizerColors.filter(Boolean)}
          setVisualizerColors={setVisualizerColors}
          addColorToPalette={addColorToPalette}
          isVisualizerOpen={false}
          toggleVisualizer={() => setActiveSection('visualizer')}
          customPalette={customPalette}
          setCustomPalette={setCustomPalette}
          onSaveCustomPalette={handleSaveCustomPalette}
          onClearCustomPalette={handleClearCustomPalette}
          onExportCustomPaletteToVisualizer={handleExportCustomPaletteToVisualizer}
        />

        {/* Command Palette */}
        <CommandPalette
          isOpen={isCommandPaletteOpen}
          onClose={() => setIsCommandPaletteOpen(false)}
          commands={commands}
        />

        {/* Info Modal */}
        {isInfoModalOpen && (
          <InfoModal onClose={() => setIsInfoModalOpen(false)} />
        )}

        {/* Footer - Hidden on info, palettes, builder, extractor, saved, and visualizer screens */}
        {activeSection !== 'info' && activeSection !== 'palettes' && activeSection !== 'builder' && activeSection !== 'extractor' && activeSection !== 'saved' && activeSection !== 'visualizer' && <Footer />}
        <OnboardingOverlay isActive={isOnboardingActive} onClose={() => setIsOnboardingActive(false)} />

        {/* Back to Top Button */}
        <BackToTop threshold={300} />
      </motion.div>

      {/* Loading Screen */}
      <LoadingScreen isLoading={isLoading} />
    </>
  );
};

export default ColorApp;
